import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { type ReactNode, useMemo } from 'react';

import { PopoutTree } from '../../../shared/components/tree/PopoutTree/PopoutTree';
import type { TreeData } from '../../../shared/components/tree/useTreeApi';
import { countiesForState } from '../data/us-counties';
import { geoFromFips } from '../data/us-geo';
import { US_STATES } from '../data/us-states';
import type { SelectedGeo } from '../types';

// State → county tree over the hardcoded census FIPS tables. Node ids
// are the FIPS themselves (2-digit states, 5-digit counties), so a
// selection maps straight back to a SelectedGeo.
const GEO_TREE_DATA: TreeData[] = US_STATES.map((state) => ({
  id: state.fips,
  name: state.name,
  children: countiesForState(state.fips).map((county) => ({
    id: county.fips,
    name: county.name,
  })),
}));

// Drill-level labels shown above each popout panel (root = states, the
// drilled-in panel = that state's counties).
const GEO_BRANCHES = [{ label: 'State' }, { label: 'County' }];

interface GeoTreePickerProps {
  selectedGeo: SelectedGeo | null;
  onSelect: (geo: SelectedGeo) => void;
  /** Popover trigger element. */
  children: ReactNode;
}

export function GeoTreePicker({
  selectedGeo,
  onSelect,
  children,
}: GeoTreePickerProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const value = useMemo(
    () => (selectedGeo ? [selectedGeo.fips] : []),
    [selectedGeo]
  );

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      isLazy
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        w="300px"
        bg="white"
        borderRadius="10px"
        boxShadow="0 24px 48px -12px rgba(20,30,55,.28)"
        // Drilled-in county panels float outside the 300px popover — don't clip.
        overflow="visible"
        p="10px"
        data-testid="geo-tree-picker"
      >
        <PopoutTree
          data={GEO_TREE_DATA}
          branches={GEO_BRANCHES}
          singleSelect
          value={value}
          onChange={(ids) => {
            const fips = ids[ids.length - 1];
            if (!fips) return; // ignore deselect — the page always keeps a geo once picked
            const geo = geoFromFips(fips);
            if (!geo) return;
            onSelect(geo);
            onClose();
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
